import React, {useCallback, useContext, useRef, useState} from 'react';


import {MessageContext} from 'src/contexts/message-context';
import {useEditTableEventTracking} from 'src/hooks/user/useEditTableEventTracking';
import {TemplateType as TemplateTypes} from 'src/openapi/admin/models/TemplateType';

import {FlatButton} from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Dropdown, type DropdownProps} from '@pb/kit-component-dropdown';
import Icon, {Icons} from '@pb/kit-component-icon';
import {useShown} from '@pb/kit-hooks-use-shown';

import {AddTemplateModal} from './add-template-modal';
import {type ColumnAdapter, defaultColumnAdapter} from './column-adapter';
import {EditSearchColumnsBody} from './edit-search-columns-body';
import {EditSearchColumnsModal} from './edit-search-columns-modal';
import {EditSortingModal} from './edit-sorting-modal';
import {RemoveTemplateModal} from './remove-template-modal';

import type {GroupSingleOption, OptionGroups, OptionTab} from '@pb/kit-component-modal-options';
import type {SearchTemplateColumn, SortRowData} from 'src/modules/advanced-search-result/typedef';
import type {SearchType} from 'src/openapi/admin/models/SearchType';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';


type Props = {
    columnAdapter?: ColumnAdapter,
    userId: number,
    isLoading: boolean,
    searchType: SearchType,
    place: string,

    templates: UserSearchTemplate[],
    activeTemplate: UserSearchTemplate,

    sortingDataMap: Record<string, SortRowData>,
    limitedSortColumns: string[],
    noSortColumns: string[],
    columns: Record<string, string>,
    columnsMap: SearchTemplateColumn[],
    items: OptionTab[],
    allOptions: GroupSingleOption[],
    optionGroups: OptionGroups,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    optionSettings: any,

    onUpdateTemplates: () => void,
    onUpdateColumns: () => void,
    onSetActiveTemplate: (template: UserSearchTemplate) => void,
};

const DropdownMenuBody: DropdownProps['bodyComponent'] = ({children}) => (<>{children}</>);

export const EditSearchColumns = (props: Props) => {
    const {
        columnAdapter = defaultColumnAdapter,
        userId,
        isLoading,
        searchType,
        templates,
        place,
        activeTemplate,
        sortingDataMap,
        limitedSortColumns,
        noSortColumns,
        columns,
        columnsMap,
        items,
        allOptions,
        optionGroups,
        optionSettings,
        onUpdateTemplates,
        onUpdateColumns,
        onSetActiveTemplate,
    } = props;

    const [shown, setShown] = useState(false);
    const openEditAfterAdd = useRef(false);
    const [addModalKey, setAddModalKey] = useState(0);
    const [addModalPreselect, setAddModalPreselect] = useState<number | null>(null);
    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);

    const [removedTemplate, setRemovedTemplate] = useState<UserSearchTemplate | null>(null);

    const {shown: editModalShown, onClick: openEditModal, onClose: closeEditModal} = useShown();
    const {shown: addModalShown, onClick: openAddModal, onClose: onCloseAddModal} = useShown();
    const {shown: sortingModalShown, onClick: openSortingModal, onClose: closeSortingModal} = useShown();

    const {
        createLayoutSubmit,
        renameLayoutSubmit,
        removeLayoutSubmit,
        editColumnsSubmit,
        editSortingSubmit,
    } = useEditTableEventTracking();

    const getAnchor = useCallback<NonNullable<DropdownProps['anchor']>>(({anchorRef, positionRef, getAnchorProps}) => (
        <FlatButton
            startIcon={<Icon type={Icons.TABLE}/>}
            endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
            disabled={isLoading}
            ref={anchorRef}
            {...getAnchorProps()}>
            <b>Edit Table</b>
        </FlatButton>
    ), [isLoading]);

    const openEditActiveTemplate = useCallback(() => {
        setShown(false);
        if (activeTemplate.templateType !== TemplateTypes.DEFAULT) {
            openEditModal();
            return;
        }
        openEditAfterAdd.current = true;
        setAddModalPreselect(activeTemplate.templateId);
        setAddModalKey(k => k + 1);
        openAddModal();
    }, [openEditModal, openAddModal, activeTemplate.templateType, activeTemplate.templateId]);

    const openRemoveTemplate = useCallback((template: UserSearchTemplate) => {
        setRemovedTemplate(template);
        setShown(false);
    }, [setRemovedTemplate]);

    const openAddTemplate = useCallback(() => {
        setAddModalPreselect(null);
        setAddModalKey(k => k + 1);
        openAddModal();
        setShown(false);
    }, [openAddModal]);

    const openEditSortingModal = useCallback(() => {
        openSortingModal();
        setShown(false);
    }, [openSortingModal]);

    const closeAddModal = useCallback(() => {
        createLayoutSubmit({place, submit: false, success: true});
        openEditAfterAdd.current = false;
        onCloseAddModal();
    }, [place, createLayoutSubmit, onCloseAddModal]);

    const onFailAddLayout = useCallback((error: string) => {
        createLayoutSubmit({place, submit: true, success: false});
        openEditAfterAdd.current = false;
        setErrorMessage(error);
        onCloseAddModal();
    }, [place, createLayoutSubmit, setErrorMessage, onCloseAddModal]);

    const onDoneAddLayout = useCallback(async (newTemplate: UserSearchTemplate) => {
        createLayoutSubmit({place, submit: true, success: true});
        await onSetActiveTemplate(newTemplate);
        await onUpdateTemplates();
        setSuccessMessage('Layout added');
        onCloseAddModal();
        if (openEditAfterAdd.current) {
            openEditAfterAdd.current = false;
            openEditModal();
        }
    }, [place, createLayoutSubmit, onSetActiveTemplate, onUpdateTemplates, setSuccessMessage,
        onCloseAddModal, openEditModal]);

    const onFailRenameLayout = useCallback((error: string) => {
        renameLayoutSubmit({place, submit: true, success: false});
        setErrorMessage(error);
    }, [place, renameLayoutSubmit, setErrorMessage]);

    const onDoneRenameLayout = useCallback(() => {
        renameLayoutSubmit({place, submit: true, success: true});
        onUpdateTemplates();
    }, [place, onUpdateTemplates, renameLayoutSubmit]);

    const closeRemoveModal = useCallback(() => {
        removeLayoutSubmit({place, submit: false, success: true});
        setRemovedTemplate(null);
    }, [place, setRemovedTemplate, removeLayoutSubmit]);

    const onFailRemoveLayout = useCallback((error: string) => {
        removeLayoutSubmit({place, submit: true, success: false});
        setErrorMessage(error);
        setRemovedTemplate(null);
    }, [place, removeLayoutSubmit, setErrorMessage]);

    const onDoneRemoveLayout = useCallback(() => {
        removeLayoutSubmit({place, submit: true, success: true});
        onUpdateTemplates();
        setSuccessMessage('Layout removed');
        setRemovedTemplate(null);
    }, [place, onUpdateTemplates, removeLayoutSubmit, setSuccessMessage]);

    const closeEditColumnsModal = useCallback(() => {
        editColumnsSubmit({place, submit: false, success: true});
        closeEditModal();
    }, [place, closeEditModal, editColumnsSubmit]);

    const onFailEditColumns = useCallback((error: string) => {
        editColumnsSubmit({place, submit: true, success: false});
        setErrorMessage(error);
        closeEditModal();
    }, [place, closeEditModal, editColumnsSubmit, setErrorMessage]);

    const onDoneEditColumns = useCallback(() => {
        editColumnsSubmit({place, submit: true, success: true});
        onUpdateColumns();
        setSuccessMessage('Layout saved');
        closeEditModal();
    }, [place, closeEditModal, setSuccessMessage, onUpdateColumns, editColumnsSubmit]);

    const closeEditSortingModal = useCallback(() => {
        editSortingSubmit({place, submit: false, success: true});
        closeSortingModal();
    }, [place, closeSortingModal, editSortingSubmit]);

    const onFailEditSorting = useCallback((error: string) => {
        editSortingSubmit({place, submit: true, success: false});
        setErrorMessage(error);
        closeSortingModal();
    }, [place, closeSortingModal, editSortingSubmit, setErrorMessage]);

    const onDoneEditSorting = useCallback(() => {
        editSortingSubmit({place, submit: true, success: true});
        onUpdateColumns();
        setSuccessMessage('Default sorting saved');
        closeSortingModal();
    }, [place, closeSortingModal, editSortingSubmit, onUpdateColumns, setSuccessMessage]);

    const onDoneCopyLayout = useCallback(async (newTemplate: UserSearchTemplate) => {
        createLayoutSubmit({place, submit: true, success: true});
        await onSetActiveTemplate(newTemplate);
        await onUpdateTemplates();
        setSuccessMessage('Layout added');
        setShown(false);
    }, [place, createLayoutSubmit, onSetActiveTemplate, onUpdateTemplates, setSuccessMessage]);

    const onFailCopyLayout = useCallback((error: string) => {
        createLayoutSubmit({place, submit: true, success: false});
        setErrorMessage(error);
        setShown(false);
    }, [place, createLayoutSubmit, setErrorMessage]);

    const setDefaultTemplate = useCallback(async () => {
        const defaultTemplate = templates
            .find(template =>
                template.templateType === TemplateTypes.DEFAULT &&
                template.searchType === searchType,
            );

        await onSetActiveTemplate(defaultTemplate || templates[0]);
    }, [templates, searchType, onSetActiveTemplate]);

    const setActiveTemplate = useCallback(async (template: UserSearchTemplate) => {
        await onSetActiveTemplate(template);
        await onUpdateTemplates();
        setShown(false);
    }, [onSetActiveTemplate, onUpdateTemplates]);

    return (
        <>
            <Dropdown {...{
                maxHeight: 500,
                anchor: getAnchor,
                shown,
                hideOnBodyLeave: false,
                setShown,
                disabled: isLoading,
                bodyComponent: DropdownMenuBody,
            }}>
                <EditSearchColumnsBody
                    userId={userId}
                    activeTemplate={activeTemplate}
                    templates={templates}
                    setActiveTemplate={setActiveTemplate}
                    openEditActiveTemplate={openEditActiveTemplate}
                    openAddModal={openAddTemplate}
                    openSortingModal={openEditSortingModal}
                    openRemoveTemplate={openRemoveTemplate}
                    onRenameDone={onDoneRenameLayout}
                    onRenameFail={onFailRenameLayout}
                    onRenameCancel={() => renameLayoutSubmit({place, submit: false, success: true})}
                    onCopyDone={onDoneCopyLayout}
                    onCopyFail={onFailCopyLayout}
                    onCopyCancel={() => createLayoutSubmit({place, submit: false, success: true})}
                />
            </Dropdown>
            <Dialog shown={editModalShown} setShown={closeEditColumnsModal}>
                <EditSearchColumnsModal
                    columnAdapter={columnAdapter}
                    searchType={searchType}
                    templates={templates}
                    columns={columns}
                    columnsMap={columnsMap}
                    items={items}
                    allOptions={allOptions}
                    optionGroups={optionGroups}
                    optionSettings={optionSettings}
                    activeTemplate={activeTemplate}
                    onClose={closeEditColumnsModal}
                    onDone={onDoneEditColumns}
                    onFail={onFailEditColumns}
                />
            </Dialog>
            <Dialog shown={addModalShown} setShown={closeAddModal} wrapperProps={{style: {width: 320}}}>
                <AddTemplateModal
                    key={addModalKey}
                    templates={templates}
                    userId={userId}
                    searchType={searchType}
                    preselectedTemplateId={addModalPreselect}
                    onClose={closeAddModal}
                    onDone={onDoneAddLayout}
                    onFail={onFailAddLayout}
                />
            </Dialog>
            <Dialog shown={!!removedTemplate} setShown={closeRemoveModal} wrapperProps={{style: {width: 320}}}>
                <RemoveTemplateModal
                    removedTemplate={removedTemplate}
                    setDefaultTemplate={setDefaultTemplate}
                    onClose={closeRemoveModal}
                    onDone={onDoneRemoveLayout}
                    onFail={onFailRemoveLayout}
                />
            </Dialog>
            <Dialog shown={sortingModalShown} setShown={closeEditSortingModal}>
                <EditSortingModal
                    columnAdapter={columnAdapter}
                    sortingDataMap={sortingDataMap}
                    activeTemplate={activeTemplate}
                    columns={columns}
                    limitedSortColumns={limitedSortColumns}
                    noSortColumns={noSortColumns}
                    columnsMap={columnsMap}
                    onClose={closeEditSortingModal}
                    onDone={onDoneEditSorting}
                    onFail={onFailEditSorting}
                />
            </Dialog>
        </>
    );
};
